<template>
  <multiselect
    v-model="selectedTags"
    @search-change="getSearches"
    :options="searchedTags"
    :multiple="true"
    :close-on-select="false"
    :clear-on-select="true"
    :preserve-search="false"
    :loading="isLoading"
    :placeholder="isUploadingProject ? 'Select tags to add to your project...' : 'Search to filter results...'"
    label="search"
    track-by="search"
    open-direction="bottom"
    :preselect-first="true"
  >

    <template slot="option" slot-scope="props">
      <div class="option__desc">
        <span class="option__title">{{ props.option.label }}&nbsp;&nbsp;</span>
        <span class="option__small label label-default" v-show="props.option.description !== 'previous'">
          {{ props.option.description }}
        </span>
      </div>
    </template>

    <template slot="clear" slot-scope="props">
      <button class="btn btn-link multiselect__clear" @mousedown.prevent.stop="clearAll(props.search)" :disabled="!selectedTags.length"><span class="label label-default">Clear All</span></button>
    </template>

    <span slot="noResult">Oops! No elements found. Consider changing the search query.</span>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
  /**
   * @file: Search.vue
   * @description a component to search for tags. Uses vue-multiselect (https://vue-multiselect.js.org/) with
   * recycled code from the tokchi-search.js mixin (now dead code). Used for any tag based searching (gallery, creations/mixology, and color match).
   */

  name: 'Search',

  components: { Multiselect },

  props: {
    tags: {
      type: Array,
      default: () => [],
    },
    tab: {
      type: String,
      default: '',
    },
    order: {
      type: String,
      default: '',
    },
    isUploadingProject: {
      type: Boolean,
      default: false,
    },
    setSearch: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      selectedTags: this.tags || [],
      searchedTags: [],
      isLoading: false,
      queryParams: {},
      searchType: "",
      bookmarked: window._PR?.bookmarked || false,
      owned: window._PR?.owned || false,
      search: {},
      applicatorName: '',
    }
  },

  computed: {
    selectedHashTags() {
      return this.getSelectedEntity("hashtag", "label");
    },
    selectedLocations() {
      return this.getSelectedEntity("location", "id");
    },
    selectedApplicators() {
      return this.getSelectedEntity("applicator", "id");
    },
    selectedColorsIds() {
      return this.getSelectedEntity("color_category", "id");
    },
    selectedBookmarked() {
      return this.getSelectedEntity("bookmarked", "type");
    },
    selectedOwned() {
      return this.getSelectedEntity("owned", "type");
    },
    allSelectedColors() {
      return this.getSelectedColorEntity();
    },
    pageHasQueryParams() {
      return window.location.search.length > 0;
    },
  },

  created() {
    // Sets the search type for use in the 'baseUrl' of the API search call.
    switch (this.$root.pageName) {
      case "Gallery":
        this.searchType = "gallery-search";
        break;
      case "Creations":
        this.searchType = "creation-search";
        break;
      case "Color Match":
        this.searchType = "color-match-search";
        break;
      case "Profile":
        this.searchType = "gallery-search";
        break;
    }

    /**
     * the following check 👇 evaluates whether the page has query params (ON EACH PAGE LOAD). If so, it then will either trigger an api search to set the necessary tags, otherwise it will add the item to the selectedTags.
     */

    if (this.pageHasQueryParams) {
      const urlParams = new URLSearchParams(window.location.search);
      const searchIds = [
        {
          id: 'htids',
          type: 'hashtag',
        },
        {
          id: 'places',
          type: 'location',
        },
        {
          id: 'apids',
          type: 'applicator',
        },
        {
          id: 'color',
          type: 'color_category',
        },
        {
          id: 'bookmarked',
          type: 'bookmarked',
        },
        {
          id: 'owned',
          type: 'owned',
        },
      ];

      window.location.search
        .replace('?', '')
        .split('&')
        .map(param => param.split('='))
        .reduce((values, [key, value]) => {
          values[key] = value;
          return this.search = values;
        }, {});

      const queryParamType = Object.keys(this.search);
      const loadedTags = [];

      queryParamType.forEach(type => {
        // escape items that aren't relevant to the search
        if (type === 'tab' || type === 'orderby') {
          return;
        }

        urlParams.get(type.toString()).split(',').forEach(tag => {
          const paramType = searchIds.find(id => id.id === type.toString()).type;
          loadedTags.push({
            id: tag,
            label: paramType === 'bookmarked' ? 'My Bookmarks' : paramType === 'applicator' ? 'Applicator' : tag,
            type: paramType,
            search: paramType === 'bookmarked' ? 'My Bookmarks' : paramType === 'applicator' ? 'Applicator' : tag,
          });
        });
      });

      // NOTE 1: non-hashtag items are different then any of the other search types. This isn't ideal... but since the api handles each type differently (colors use ID where hashtags use STRING) we're needing to pre-fetch/set any non-hashtag query params (this can be expensive; especially if multiple non-hashtag params are present). Suggest optimizing the api search so the front-end doesn't need to do a bunch of work to get the data.

      // NOTE 2: also, applicator searches will return the ID on page load, rather than the label, as a result of the weird api search.

      loadedTags.forEach(tag => {
        //sets the applicator name
        if (tag.type === 'applicator') {
          this.getApplicatorName(tag.id).then(() => {
            tag.label = this.applicatorName;
          });
        }

        // sets the color name
        if (tag.type === 'color_category' || tag.type === 'location') {
          this.getSearches(tag.label.split('_')[1]).then(() => {
            if (Object.keys(this.searchedTags).length !== 0) {
              this.selectedTags.push(this.searchedTags[0]);
            }

            if (Object.keys(this.searchedTags).length === 0) {
              window.history.pushState("", "", window.location.pathname);
            }
          });
        } else {
          // sets the other tags
          this.selectedTags.push(tag);
        }
      });
    }
  },

  watch: {
    // watch for changes to the selectedTags (searches) array and update the query params + projects accordingly.
    selectedTags() {
      const urlHasBookmark = window.location.search.includes('bookmarked=true');
      const tagsHaveBookmark = this.selectedTags.filter(tag => tag.type === 'bookmarked').length > 0;

      if (urlHasBookmark && !tagsHaveBookmark) {
        let params = new URLSearchParams(window.location.search);
        params.delete('bookmarked');
        this.bookmarked = false;
      }

      this.setQueryParams();
      this.updateUrl();
    },

    order() {
      this.updateUrl();
    },

    tab() {
      this.updateUrl();
    },

    isUploadingProject() {
      if (this.isUploadingProject) {
        this.selectedTags = [];
      }

      if (!this.isUploadingProject) {
        this.clearAll();
      }
    },
  },

  methods: {
    async getColorSkus(query) {
      this.isLoading = true;
      await axios.get(`/api/proxify/domains/prismatic/shop/products?allcats=pris_all_colors&fields=id,name,sku&q=${query}`)
        .then(response => {
          this.searchedTags = response.data.data.map(tag => {
            return {
              id: tag.id,
              label: tag.name,
              description: tag.sku,
              type: 'project_colors',
              search: tag.name + ' ' + tag.sku,
            }
          });
        })
        .catch(error => {
          console.log(error);
        });
      this.isLoading = false;
    },

    /**
     * @function getApplicatorName
     * @description gets the name of the applicator from the applicator id.
     * @param {String} id - the id of the applicator.
     */
    async getApplicatorName(id) {
      this.isLoading = true;

      await axios.get(`/api/proxify/user-public-profiles/${id}?fields=company_name`)
        .then(response => {
          this.applicatorName = response.data.company_name;
        })
        .catch(error => {
          console.log(error);
        });

        this.isLoading = false;
    },

    /**
     * @function getSearches
     * @description GETs a list of keyword searches from the api.
     * @param {String} query - the query to search for.
     */
    async getSearches(query) {
      this.isLoading = true;

      if (this.setSearch === 'project_colors') {
        this.getColorSkus(query);
        return;
      }
      let projectUploadParams = '';
      if (this.isUploadingProject) {
        projectUploadParams = '&suggest_new=true&type=hashtag';
      }
      await axios.get(`/api/proxify/domains/prismatic/${this.searchType}/tokenizer?phrase=${query}${projectUploadParams}`)
        .then(response => {
          this.searchedTags = response.data.map(tag => {
            return {
              id: tag.id,
              label: tag.label,
              type: tag.type,
              code: 'pris_' + tag.label.toLowerCase(),
              description: tag.description,
              checked: tag.type === 'color_category' ? true : false,
              search: tag.label,
            }
          });
        })
        .catch(error => {
          console.log(error);
        });

        this.isLoading = false;
    },

    /**
     * @function setQueryParams
     * @description legacy code to set query params to fetch the correct projects in gallery.js.
     */
    setQueryParams() {
      if (this.selectedColorsIds) {
        this.queryParams.cids = this.selectedColorsIds.string;
      } else {
        this.$delete(this.queryParams, "cids");
      }

      if (this.selectedHashTags.data.length) {
        this.queryParams.htids = this.selectedHashTags.string;
      } else {
        this.$delete(this.queryParams, "htids");
      }

      if (this.selectedApplicators.data.length) {
        this.queryParams.apids = this.selectedApplicators.string;
      } else {
        this.$delete(this.queryParams, "apids");
      }

      if (this.selectedLocations.data.length) {
        this.queryParams.places = this.selectedLocations.string;
      } else {
        this.$delete(this.queryParams, "places");
      }

      if (this.bookmarked) {
        this.queryParams.bookmarked = this.bookmarked;
      } else if (this.selectedBookmarked.data.length) {
        this.queryParams.bookmarked = true;
      } else {
        this.$delete(this.queryParams, "bookmarked");
      }

      if (this.owned) {
        this.queryParams.owned = this.owned;
      } else if (this.selectedOwned.data.length) {
        this.queryParams.owned = true;
      } else {
        this.$delete(this.queryParams, "owned");
      }

      // send the query params and selected tags to gallery.js.
      this.$emit('search', this.queryParams, this.selectedTags);
    },

    /**
     * @function getSelectedEntity
     * @description legacy code to set the computed property.
     */
    getSelectedEntity(entityType, key) {
      const entities = this.selectedTags.filter(token => token.type === entityType);

      return {
        data: entities,
        string: entities.map(entity => entity[key]).join(",")
      };
    },

    /**
     * @function getSelectedColorEntity
     * @description legacy code to set the computed property.
     */
    getSelectedColorEntity() {
      let entities = [];
      let selectedSearchColors = this.selectedTags.filter(token => token.type === "color_category");
      let selectedSearchColorCodes = selectedSearchColors.map(entity => entity["code"]);

      if (entities.length) {
        entities.concat(selectedSearchColorCodes);
      } else {
        entities = selectedSearchColorCodes;
      }

      return {
        data: entities,
        string: entities.join(",")
      };
    },

    /**
     * @function getQueryStringForUrl
     * @description legacy code to set the computed property.
     */
    getQueryStringForUrl() {
      let urlString = "?";
      let urlHasFragments = urlString.length > 1;

      // prevent query params from being added to the url if the project upload is open.
      if (this.isUploadingProject) {
        return;
      }

      if (this.queryParams.cids) {
        urlString += urlHasFragments ? "&" : "";
        urlString += `color=${this.allSelectedColors.string.replace(/,/g, "%2C")}`;
      }

      urlHasFragments = urlString.length > 1;
      if (this.queryParams.anycat) {
        urlString += urlHasFragments ? "&" : "";
        urlString += `color=${this.queryParams.anycat.replace(/,/g, "%2C")}`;
      }

      urlHasFragments = urlString.length > 1;
      if (this.queryParams.htids) {
        urlString += urlHasFragments ? "&" : "";
        urlString += `htids=${this.queryParams.htids.replace(/,/g, "%2C")}`;
      }

      urlHasFragments = urlString.length > 1;
      if (this.queryParams.apids) {
        urlString += urlHasFragments ? "&" : "";
        urlString += `apids=${this.queryParams.apids.replace(/,/g, "%2C")}`;
      }

      urlHasFragments = urlString.length > 1;
      if (this.queryParams.places) {
        urlString += urlHasFragments ? "&" : "";
        urlString += `places=${this.queryParams.places.replace(/,/g, "%2C")}`;
      }

      urlHasFragments = urlString.length > 1;
      if (this.queryParams.bookmarked) {
        urlString += urlHasFragments ? "&" : "";
        urlString += `bookmarked=${this.queryParams.bookmarked}`;
      }

      urlHasFragments = urlString.length > 1;
      if (this.queryParams.owned) {
        urlString += urlHasFragments ? "&" : "";
        urlString += `owned=${this.queryParams.owned}`;
      }

      if (this.tab) {
        urlString += urlHasFragments ? "&" : "";
        urlString += `tab=${this.tab}`;
      }

      if (this.order) {
        urlString += `&orderby=${this.order}`;
      }

      if (urlString.length === 1) {
        // There isn't anything apart from a single ?
        urlString = window.location.href.split("?")[0];
      }

      return urlString;
    },

    updateUrl() {
      const queryString = this.getQueryStringForUrl();
      window.history.pushState("", "", queryString);
    },

    clearAll() {
      this.selectedTags = [];
      this.searchedTags = [];
      this.isLoading = false;
      this.queryParams = {};
      this.search = {};
      let params = new URLSearchParams(window.location.search);
      params.delete('bookmarked');
    }
  }
}
</script>
